import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import type { UserLibraryClipDto, AudioClipDto } from '../dtos'

// User's personal clip library. Only AI-generated clips can be saved — we
// store the prompt + MIDI JSON + original BPM/duration. Dropping a library
// clip onto a track re-renders the MIDI through the VST at the destination
// mix's tempo (note times scaled by BPM ratio), so audio storage costs
// land on the destination band's quota, not the user library.
export const useUserLibraryStore = defineStore('userLibrary', () => {
  const userStore = useUserStore()
  const clips = ref<UserLibraryClipDto[]>([])
  const loading = ref(false)

  async function fetch() {
    if (!userStore.user) return
    loading.value = true
    try {
      const token = await userStore.user.getIdToken()
      const res = await api.get('/users/me/library-clips', {
        headers: { Authorization: `Bearer ${token}` }
      })
      clips.value = res.data
    } catch (e) {
      console.error('Failed to fetch library clips:', e)
    } finally {
      loading.value = false
    }
  }

  async function addFromClip(clipId: number, name: string): Promise<UserLibraryClipDto | null> {
    if (!userStore.user) return null
    const token = await userStore.user.getIdToken()
    const res = await api.post('/users/me/library-clips', { clipId, name }, {
      headers: { Authorization: `Bearer ${token}` }
    })
    clips.value = [res.data, ...clips.value]
    return res.data
  }

  async function remove(libraryClipId: number): Promise<void> {
    if (!userStore.user) return
    const token = await userStore.user.getIdToken()
    await api.delete(`/users/me/library-clips/${libraryClipId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    clips.value = clips.value.filter(c => c.id !== libraryClipId)
  }

  // Drops a library clip onto a track. Backend retargets the MIDI to the
  // destination mix's BPM, re-renders via VST, and creates a fresh AudioClip
  // + ClipPrompt. Returns the new AudioClipDto for the mix's local state.
  async function addToTrack(trackId: number, libraryClipId: number, timelineStart: number): Promise<AudioClipDto | null> {
    if (!userStore.user) return null
    const token = await userStore.user.getIdToken()
    const res = await api.post(`/mixes/tracks/${trackId}/clips/from-library`, {
      libraryClipId, timelineStart
    }, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return res.data as AudioClipDto
  }

  function clear() {
    clips.value = []
    loading.value = false
  }

  return { clips, loading, fetch, addFromClip, remove, addToTrack, clear }
})
